import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as EditIcon } from '../../../assets/icons/edit-pencil.svg';
import { ReactComponent as EyeIcon } from '../../../assets/icons/eye-icon.svg';
import { ReactComponent as RfidTagIcon } from '../../../assets/icons/rfid-icon.svg';
import { ApiRequest } from '../../../interface/common.interface';
import { CardPool, RfidToken } from '../../../interface/rfid-management.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import {
  disableTokenFilterApplied,
  fetchTokenFiltersApi,
  fetchTokensApi,
  setSelectedToken,
  setTokenSortData,
  updateTokenFilters,
} from '../../../redux/slices/RfidTokenSlice';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import { GetParametersFromCommonFilters } from '../../../shared-functions/CommonFilters';
import ConvertUTCtoDateTime from '../../../shared-functions/ConvertUTCtoDateTime';
import { removeEmptyKeys } from '../../../shared-functions/DateTime';
import { exportCSV, exportPDF } from '../../../shared-functions/ExportFile';
import rfidApi from '../../../utils/api/rfid';

interface TokensListProperties {
  onOpenAddModal?: () => void;
  onOpenDetailsModal: (token: RfidToken, isEdit?: boolean) => void;
  onOpenEditModal?: (token: RfidToken) => void;
  onOpenDeactivateModal?: (token: RfidToken) => void;
  onOpenRechargeModal?: (group: CardPool) => void;
  rightTopContent?: React.JSX.Element;
  sidePanelEnabled?: boolean;
  sidePanelManualContent?: React.JSX.Element;
  sidePanelTopLeftContent?: React.JSX.Element;
  onSidePanelClose?: () => void;
}

/**
 * Tokens and RFID physical cards list view component
 * @param {TokensListProperties} properties - Component properties
 * @returns {React.JSX.Element} RFID tokens list view with CustomTable
 */
function TokensList(properties: Readonly<TokensListProperties>): React.JSX.Element {
  const {
    onOpenDetailsModal,
    onOpenEditModal,
    rightTopContent,
    sidePanelEnabled,
    sidePanelManualContent,
    sidePanelTopLeftContent,
    onSidePanelClose,
  } = properties;
  const { t } = useTranslation(['devices', 'sharedComponent']);
  const dispatch = useAppDispatch();
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const {
    tokens,
    loading,
    limit,
    page,
    totalPages,
    totalResults,
    filters,
    isFilterUpdated,
    filterListLoading,
    sortBy,
    sortField,
  } = useAppSelector((state) => state.rfidTokens);

  useEffect(() => {
    if (isFilterUpdated) {
      const queryParameters = GetParametersFromCommonFilters(filters);
      dispatch(
        fetchTokensApi({
          page: 1,
          limit,
          sortBy,
          sortField,
          ...queryParameters,
        }),
      );
      dispatch(disableTokenFilterApplied());
    }
  }, [isFilterUpdated, dispatch, filters, limit, sortBy, sortField]);

  /**
   * Refreshes the tokens list
   * @returns {void}
   */
  const handleRefresh = (): void => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(
      fetchTokensApi({
        ...queryParameters,
        page,
        limit,
        sortBy,
        sortField,
      }),
    );
  };

  /**
   * Handles table sort change
   * @param {{ sortBy: 'asc' | 'desc'; sortField: string }} sortData - Sort data with field and order
   * @returns {void}
   */
  const handleSortChange = (sortData: { sortBy: 'asc' | 'desc'; sortField: string }): void => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setTokenSortData(sortData));
    dispatch(
      fetchTokensApi({
        ...queryParameters,
        sortBy: sortData.sortBy,
        sortField: sortData.sortField,
        page: 1,
        limit,
      }),
    );
  };

  /**
   * Handles pagination and page limit change
   * @param {ApiRequest} value - Pagination parameters
   * @returns {void}
   */
  const handlePageLimitChange = (value: ApiRequest): void => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(
      fetchTokensApi({
        ...queryParameters,
        page: value.page ?? page,
        limit: value.limit ?? limit,
        sortBy,
        sortField,
      }),
    );
  };

  const columns = [
    {
      label: t('rfidUid'),
      accessorKey: 'uid' as keyof RfidToken,
      // sortAllow: true,
      /**
       * Custom rendering for UID
       * @param {RfidToken} row - Current row
       * @returns {React.JSX.Element} Formatted UID block
       */
      customData: (row: RfidToken): React.JSX.Element => (
        <div
          role="presentation"
          className="rfid-table-uid-cell"
          onClick={() => {
            dispatch(setSelectedToken(row));
            onOpenDetailsModal(row, false);
          }}>
          <span className="rfid-table-uid-cell__icon">
            <RfidTagIcon />
          </span>
          <span className="rfid-table-uid-cell__text">{row.uid}</span>
        </div>
      ),
    },
    {
      label: t('customerName'),
      accessorKey: 'customerName' as keyof RfidToken,
      // sortAllow: true,
    },
    {
      label: t('cardType'),
      accessorKey: 'type' as keyof RfidToken,
      // sortAllow: true,
      /**
       * Custom rendering for Card Type
       * @param {RfidToken} row - Current row
       * @returns {React.JSX.Element} Badge for card type
       */
      customData: (row: RfidToken): React.JSX.Element => {
        const isBusiness = row.type === 'BUSINESS_TAP';
        return (
          <span
            className={`rfid-type-badge ${
              isBusiness ? 'rfid-type-badge--business' : 'rfid-type-badge--personal'
            }`}>
            {isBusiness ? t('businessTap') : t('personalTap')}
          </span>
        );
      },
    },
    {
      label: t('status'),
      accessorKey: 'status' as keyof RfidToken,
      // sortAllow: true,
      /**
       * Custom rendering for Status
       * @param {RfidToken} row - Current row
       * @returns {React.JSX.Element} Status badge
       */
      customData: (row: RfidToken): React.JSX.Element => {
        const isActive = row.status === 'ACTIVE';
        return (
          <span
            className={`rfid-status-badge ${
              isActive ? 'rfid-status-badge--active' : 'rfid-status-badge--deactivated'
            }`}>
            <span className="rfid-status-badge__dot" />
            {row.status}
          </span>
        );
      },
    },
    {
      label: t('issuedBy'),
      accessorKey: 'issuedBy' as keyof RfidToken,
      // sortAllow: true,
      /**
       * Custom rendering for Issued By
       * @param {RfidToken} row - Current row
       * @returns {React.JSX.Element} Issued by label
       */
      customData: (row: RfidToken): React.JSX.Element => (
        <span className="rfid-table-text-muted">{row.issuedBy}</span>
      ),
    },
    {
      label: t('targetLink'),
      accessorKey: 'cardPoolId' as keyof RfidToken,
      // sortAllow: false,
      /**
       * Custom rendering for Linked Target Entity
       * @param {RfidToken} row - Current row
       * @returns {React.JSX.Element} Target entity link
       */
      customData: (row: RfidToken): React.JSX.Element => {
        if (row.type === 'BUSINESS_TAP') {
          const poolId = row.cardPoolId || row.cardPool?._id || row.cardPool?.id;
          const poolName = row.cardPoolName || row.cardPool?.name || poolId || t('groupAssigned');
          return (
            <div className="rfid-table-linked-entity">
              <span className="rfid-table-linked-entity__title">{poolName}</span>
              {poolId && <span className="rfid-table-linked-entity__subtitle">{poolId}</span>}
            </div>
          );
        }
        return (
          <div className="rfid-table-linked-entity">
            <span className="rfid-table-linked-entity__title">
              {row.customerId || t('customerAppLinked')}
            </span>
            <span className="rfid-table-linked-entity__subtitle">{t('personalWallet')}</span>
          </div>
        );
      },
    },
    {
      label: t('createdDate'),
      accessorKey: 'createdAt' as keyof RfidToken,
      // sortAllow: true,
      /**
       * Custom rendering for Created Date
       * @param {RfidToken} row - Current row
       * @returns {React.JSX.Element} Created date timestamp
       */
      customData: (row: RfidToken): React.JSX.Element => (
        <span className="rfid-table-text-muted">
          {row.createdAt ? ConvertUTCtoDateTime(row.createdAt) : t('na')}
        </span>
      ),
    },
    {
      label: t('activatedAt'),
      accessorKey: 'activatedAt' as keyof RfidToken,
      // sortAllow: true,
      /**
       * Custom rendering for Activated At
       * @param {RfidToken} row - Current row
       * @returns {React.JSX.Element} Date or dash
       */
      customData: (row: RfidToken): React.JSX.Element => (
        <span className="rfid-table-text-muted">
          {row.activatedAt ? ConvertUTCtoDateTime(row.activatedAt) : t('na')}
        </span>
      ),
    },
    {
      label: t('deactivatedAt'),
      accessorKey: 'deactivatedAt' as keyof RfidToken,
      // sortAllow: true,
      /**
       * Custom rendering for Deactivated At
       * @param {RfidToken} row - Current row
       * @returns {React.JSX.Element} Date or dash
       */
      customData: (row: RfidToken): React.JSX.Element => (
        <span className="rfid-table-text-muted">
          {row.deactivatedAt ? ConvertUTCtoDateTime(row.deactivatedAt) : t('na')}
        </span>
      ),
    },
    {
      label: t('actions'),
      accessorKey: 'id' as keyof RfidToken,
      columnWidth: 100,
      // sortAllow: false,
      sticky: 'right' as const,
      /**
       * Action buttons for editing token details and viewing card details
       * @param {RfidToken} row - Current row
       * @returns {React.JSX.Element} Action buttons element
       */
      customData: (row: RfidToken): React.JSX.Element => (
        <div className="action-buttons-wrap">
          {row.status === 'ACTIVE' && (
            <div
              role="presentation"
              className="action-button"
              title={t('editCard')}
              onClick={(event) => {
                event.stopPropagation();
                dispatch(setSelectedToken(row));
                if (onOpenEditModal) {
                  onOpenEditModal(row);
                } else {
                  onOpenDetailsModal(row, true);
                }
              }}>
              <EditIcon className="icon edit" />
            </div>
          )}
          <div
            role="presentation"
            className="action-button"
            title={t('viewDetails')}
            onClick={(event) => {
              event.stopPropagation();
              dispatch(setSelectedToken(row));
              onOpenDetailsModal(row, false);
            }}>
            <EyeIcon className="icon view" />
          </div>
        </div>
      ),
    },
  ];

  const allExportColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('rfidUid'), accessorKey: 'uid' },
    { label: t('customerName'), accessorKey: 'customerName' },
    { label: t('cardType'), accessorKey: 'type' },
    { label: t('status'), accessorKey: 'status' },
    { label: t('issuedBy'), accessorKey: 'issuedBy' },
    { label: t('targetLink'), accessorKey: 'cardPoolId' },
    { label: t('createdDate'), accessorKey: 'createdAt' },
    { label: t('deactivatedAt'), accessorKey: 'deactivatedAt' },
  ];

  /**
   * Filter columns for export without hidden columns
   */
  const exportCurrentColumns = allExportColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Export RFID tokens data to CSV or PDF format
   * @param {string} type - Export file format ('csv' | 'pdf')
   * @returns {void}
   */
  const exportDataPdfOrCSV = (type: string): void => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    const payload: any = {
      ...queryParameters,
    };
    if (sortBy) payload.sortBy = sortBy;
    if (sortField) payload.sortField = sortField;

    setExportLoader(true);
    rfidApi
      .fetchTokensExport(removeEmptyKeys(payload))
      .then((response: any) => {
        const rawResults =
          response?.data?.results ||
          response?.data?.data ||
          (Array.isArray(response?.data) ? response?.data : []);
        const dataToExport =
          Array.isArray(rawResults) && rawResults.length > 0 ? rawResults : tokens;

        const headers = exportCurrentColumns.map((col) => col.label);
        const rows = dataToExport.map((row: any, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return String(index + 1);
              case 'uid':
                return row.uid || t('na');
              case 'customerName':
                return row.customerName || t('na');
              case 'type':
                return row.type === 'BUSINESS_TAP' ? t('businessTap') : t('personalTap');
              case 'status':
                return row.status || 'ACTIVE';
              case 'issuedBy':
                return row.issuedBy || t('na');
              case 'cardPoolId':
                if (row.type === 'BUSINESS_TAP') {
                  return (
                    row.cardPoolName ||
                    row.cardPool?.name ||
                    row.cardPoolId ||
                    row.cardPool?._id ||
                    row.cardPool?.id ||
                    t('groupAssigned')
                  );
                }
                return row.customerId || t('personalWallet');
              case 'createdAt':
                return row.createdAt ? ConvertUTCtoDateTime(row.createdAt) : t('na');
              case 'deactivatedAt':
                return row.deactivatedAt ? ConvertUTCtoDateTime(row.deactivatedAt) : t('na');
              default:
                return String((row as any)[col.accessorKey] || t('na'));
            }
          }),
        );

        setExportLoader(false);
        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'rfid-cards.csv');
          } else {
            exportPDF(headers, rows, 'rfid-cards.pdf');
          }
        }
      })
      .catch((error: any) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message || t('failedToExportRfidCardsData'),
            status: true,
            type: 'error',
            code: error?.response?.data?.code || 500,
          }),
        );
      });
  };

  return (
    <CustomTable
      version="v2"
      header={columns}
      data={tokens}
      loading={loading}
      limit={limit}
      page={page}
      totalPages={totalPages}
      totalResults={totalResults}
      serialNo
      sortData={{ sortBy, sortField }}
      handleSortChange={handleSortChange}
      pageLimitChange={handlePageLimitChange}
      filters={filters}
      filtersToDispatch={updateTokenFilters}
      filterListToDispatch={fetchTokenFiltersApi}
      filterListLoading={filterListLoading}
      refreshRequired
      handleRefresh={handleRefresh}
      refershCurrentPages={handleRefresh}
      hoverLabel={t('refresh')}
      sizeChangeButtonRequired
      rightTopContent={rightTopContent}
      sidePanelEnabled={sidePanelEnabled}
      sidePanelManualContent={sidePanelManualContent}
      sidePanelTopLeftContent={sidePanelTopLeftContent}
      onSidePanelClose={onSidePanelClose}
      onHiddenColumnsChange={setHiddenColumns}
      exportDataPdfOrCSV={exportDataPdfOrCSV}
      exportLoader={exportLoader}
      exportData={tokens}
      isExportRequired
    />
  );
}

export default TokensList;
